/**
 * Cross-app URLs for the Vite-based apps — console, internal admin, and the
 * Astro website (Astro runs on Vite, so `import.meta.env` works there too).
 *
 * THE single source of truth for any link that leaves the current app. Never
 * write `import.meta.env.DEV ? 'http://localhost:3001' : 'https://…'` inline:
 * the default ports are wrong in every Superset worktree (each one gets a
 * hashed port offset — console 8780, hosted 3701, website 5021, …), which is
 * exactly how the "open report" button ended up pointing at a dead :3001.
 *
 * Resolution order per origin:
 *   1. `VITE_*`   — set by docker-compose / kk.sh for console + internal
 *   2. `PUBLIC_*` — Astro's client-exposed prefix, set by kk.sh for the website
 *   3. the production default from `urlShapes.ts`
 *
 * Both prefixes are read (rather than one per app) so a component in
 * `packages/shared` renders correct links no matter which app mounts it.
 *
 * Next.js can't use this module — `import.meta.env` doesn't exist under
 * webpack. The hosted app has `apps/hosted/lib/urls.ts`, which imports the
 * same shape rules.
 */
import {
  PROD_API_URL,
  PROD_CONSOLE_URL,
  PROD_HOSTED_URL,
  PROD_MCP_URL,
  PROD_WEBSITE_URL,
  displayUrl,
  trimTrailingSlash,
  withPath,
  workspaceUrl,
  workspaceUrlAffixes,
} from './urlShapes';

const PROD_INTERNAL_URL = 'https://internal.knitknot.ai';

// Each read is a static property access so Vite/Astro inline it at build time
// (a bare `import.meta.env[key]` would work too, but only because Vite also
// defines the whole object — this form doesn't depend on that).
const pick = (...candidates: (string | undefined)[]): string | undefined =>
  candidates.find((value) => typeof value === 'string' && value.length > 0);

/** Console SPA origin (`app.knitknot.ai` in prod). */
export const CONSOLE_URL = trimTrailingSlash(
  pick(import.meta.env.VITE_CONSOLE_URL, import.meta.env.PUBLIC_CONSOLE_URL) ?? PROD_CONSOLE_URL,
);

/** Internal staff SPA origin (`internal.knitknot.ai` in prod). */
export const INTERNAL_URL = trimTrailingSlash(
  pick(import.meta.env.VITE_INTERNAL_URL, import.meta.env.PUBLIC_INTERNAL_URL) ?? PROD_INTERNAL_URL,
);

/** Hosted report app origin — the *base*, not a workspace URL. */
export const HOSTED_URL = trimTrailingSlash(
  pick(import.meta.env.VITE_HOSTED_URL, import.meta.env.PUBLIC_HOSTED_URL) ?? PROD_HOSTED_URL,
);

/** Marketing site origin (`knitknot.ai` in prod). */
export const WEBSITE_URL = trimTrailingSlash(
  pick(import.meta.env.VITE_WEBSITE_URL, import.meta.env.PUBLIC_WEBSITE_URL) ?? PROD_WEBSITE_URL,
);

/** Backend API origin. Note: the console talks to the API through its Vite
 *  proxy (`/api`), so this is only for absolute links / non-proxied fetches. */
export const API_URL = trimTrailingSlash(
  pick(import.meta.env.VITE_API_BASE_URL, import.meta.env.PUBLIC_API_BASE_URL) ?? PROD_API_URL,
);

/** MCP Streamable HTTP endpoint, trailing slash included. */
export const MCP_URL =
  pick(import.meta.env.VITE_MCP_URL, import.meta.env.PUBLIC_MCP_URL) ?? PROD_MCP_URL;

/** A path on the console, e.g. `consoleUrl('/signin')`. */
export function consoleUrl(path = ''): string {
  return withPath(CONSOLE_URL, path);
}

/** A path on the internal staff console. */
export function internalUrl(path = ''): string {
  return withPath(INTERNAL_URL, path);
}

/** A path on the marketing site, e.g. `websiteUrl('/docs/')`. */
export function websiteUrl(path = ''): string {
  return withPath(WEBSITE_URL, path);
}

/**
 * A workspace's public home on the hosted app — subdomain form in production,
 * path form locally. See `workspaceUrl` in `urlShapes.ts` for the rule.
 */
export function hostedWorkspaceUrl(slug: string, path = ''): string {
  return workspaceUrl(HOSTED_URL, slug, path);
}

/** A published report's public URL. */
export function hostedReportUrl(workspaceSlug: string, reportSlug: string): string {
  return hostedWorkspaceUrl(workspaceSlug, `/reports/${reportSlug}`);
}

/**
 * The fixed labels either side of an editable workspace slug —
 * `https://` + [slug] + `.knitknot.io` in production, `localhost:3701/` +
 * [slug] locally.
 */
export function hostedSlugAffixes(): { prefix: string; suffix: string } {
  return workspaceUrlAffixes(HOSTED_URL);
}

export { displayUrl };
